import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Loader2, Globe, Mail, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const URGENCY = [["urgent", "Urgent — Within 24 Hours"], ["week", "This Week"], ["planning", "Planning Ahead"]];

export default function ClientRequest() {
  const [q] = useSearchParams();
  const [f, setF] = useState({ name: q.get("n") || "", company: q.get("c") || "", email: q.get("e") || "", phone: q.get("p") || "", details: "", urgency: "urgent", event_code: q.get("ev") || "", hp: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await axios.post(`${API}/public/requests`, f); setDone(data); }
    catch (x) { setErr(x.response?.data?.detail || "Something Went Wrong — Please Try Again."); }
    finally { setBusy(false); }
  };
  const fld = (k, l, t = "text") => <div><Label className="text-xs">{l}</Label><Input type={t} value={f[k]} onChange={set(k)} data-testid={`request-${k}`} /></div>;
  return (
    <div className="min-h-screen bg-[#0F2347]" data-testid="client-request-page">
      <div className="mx-auto max-w-lg px-4 pb-10 pt-8">
        <div className="mb-6 text-center text-white">
          <img src="/somcore-logo-white.png" alt="Somcore" className="mx-auto h-8" />
          <p className="mt-2 text-sm text-white/80">At The Core Of Global Resources.</p>
        </div>
        <div className="rounded-2xl bg-white p-5 shadow-xl">
          {done ? (
            <div className="space-y-3 py-6 text-center" data-testid="request-done">
              <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
              <h1 className="font-heading text-xl font-bold text-[#12294B]">Our Team Has Your Request</h1>
              <p className="text-sm text-slate-600">Your Reference</p>
              <p className="font-mono text-2xl font-bold text-[#1B4383]" data-testid="request-reference">{done.reference}</p>
              <p className="text-sm text-slate-600">{done.handled_by || "Our Team"} Will Come Back To You As Soon As Possible. We've Also Emailed You A Copy.</p>
              <Link to={`/track?ref=${encodeURIComponent(done.reference)}&e=${encodeURIComponent(f.email)}`} className="inline-block rounded-lg bg-[#1B4383] px-4 py-2 text-sm font-semibold text-white hover:bg-[#16386e]" data-testid="request-track-link">Track My Request</Link>
              <div className="space-y-1.5 pt-3 text-sm text-[#1B4383]">
                <a href="https://www.somcoregroup.com" className="flex items-center justify-center gap-2 font-semibold"><Globe className="h-4 w-4" />www.somcoregroup.com</a>
                <a href="mailto:info@somcoregroup.com" className="flex items-center justify-center gap-2"><Mail className="h-4 w-4" />info@somcoregroup.com</a>
                <a href="tel:+255669097454" className="flex items-center justify-center gap-2"><Phone className="h-4 w-4" />+255 669 097 454</a>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <h1 className="font-heading text-lg font-bold text-[#12294B]">Need Something Urgently?</h1>
              <p className="text-sm text-slate-600">Tell Us What You Need And The Somcore Team Will Come Back To You As Soon As Possible.</p>
              {err && <p className="rounded-md bg-red-50 p-2 text-xs text-red-700" data-testid="request-error">{err}</p>}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{fld("name", "Your Name *")}{fld("company", "Company")}{fld("email", "Email *", "email")}{fld("phone", "Phone")}</div>
              <div><Label className="text-xs">What Do You Need? *</Label><Textarea rows={5} value={f.details} onChange={set("details")} placeholder="Part Numbers, Brands, Quantities, Site And Any Deadline" data-testid="request-details" /></div>
              <div>
                <Label className="text-xs">How Urgent Is It?</Label>
                <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {URGENCY.map(([k, l]) => <button type="button" key={k} onClick={() => setF({ ...f, urgency: k })} className={`rounded-lg border px-2 py-2 text-xs font-semibold ${f.urgency === k ? "border-[#1B4383] bg-[#1B4383] text-white" : "border-border text-slate-700"}`} data-testid={`request-urgency-${k}`}>{l}</button>)}
                </div>
              </div>
              <input value={f.hp} onChange={set("hp")} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
              <Button type="submit" disabled={busy || !f.name.trim() || !f.email.trim() || !f.details.trim()} className="w-full bg-[#1B4383] hover:bg-[#16386e]" data-testid="request-submit">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Send My Request
              </Button>
              <p className="pt-1 text-center text-xs text-slate-500">Already Sent A Request? <Link to={`/track${f.email ? `?e=${encodeURIComponent(f.email)}` : ""}`} className="font-semibold text-[#1B4383] hover:underline" data-testid="request-track-existing">Track It Here</Link></p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
